/**
 * Callable wrappers for Payment Links (backend/functions/src/paymentLinks).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type {
  PaymentLinkConfigInput,
  PaymentLinkConfirmResponse,
  PaymentLinkStatus,
  PublicPaymentLinkResponse,
} from '@masonic-tools/shared-types';

export interface SaveLinkResult {
  linkId: string;
  shortUrl: string;
  shortenerUsed: boolean;
  targetUrl?: string;
}

function call<Req, Res>(name: string) {
  return async (data: Req): Promise<Res> => (await httpsCallable<Req, Res>(functions, name)(data)).data;
}

export const createPaymentLink = call<PaymentLinkConfigInput & { unitId: string }, SaveLinkResult>('createPaymentLink');
export const updatePaymentLink = call<PaymentLinkConfigInput & { unitId: string; linkId: string }, SaveLinkResult>('updatePaymentLink');
export const setPaymentLinkStatus = call<{ unitId: string; linkId: string; status: PaymentLinkStatus }, { linkId: string; status: PaymentLinkStatus }>(
  'setPaymentLinkStatus',
);
export const retryPaymentLinkShortUrl = call<{ unitId: string; linkId: string }, { shortUrl: string; shortenerUsed: boolean; error?: string }>(
  'retryPaymentLinkShortUrl',
);
export const resendPaymentLinkReceipt = call<{ unitId: string; linkId: string; paymentId: string }, { sent: boolean }>('resendPaymentLinkReceipt');
export const logPaymentLinkExport = call<{ unitId: string; linkIds: string[]; format: string; count: number }, { ok: boolean }>(
  'logPaymentLinkExport',
);
export const assertUnitExportAllowed = call<{ unitId: string }, { ok: boolean; creditBalance: number }>('assertUnitExportAllowed');

export const getPublicPaymentLink = call<{ unitId: string; linkId: string }, PublicPaymentLinkResponse>('getPublicPaymentLink');

export interface CheckoutRequest {
  unitId: string;
  linkId: string;
  amountPence: number;
  payerName?: string;
  payerEmail?: string;
  message?: string;
  giftAid?: {
    confirmed: boolean;
    title?: string;
    firstName: string;
    lastName: string;
    houseNameOrNumber: string;
    postcode: string;
  };
}

export const createPaymentLinkCheckout = call<CheckoutRequest, { paymentId: string; checkoutUrl: string; totalChargedPence: number }>(
  'createPaymentLinkCheckout',
);
export const confirmPaymentLinkPayment = call<
  { unitId: string; linkId: string; paymentId: string; cancelled?: boolean; manual?: boolean },
  PaymentLinkConfirmResponse
>('confirmPaymentLinkPayment');

/** Message and field from a callable error (the server sends `details.field` for validation errors). */
export function callableErrorInfo(error: unknown, fallback: string): { message: string; field?: string; code?: string } {
  const e = error as { message?: string; code?: string; details?: { field?: string } };
  const message = e?.message && !/^internal$/i.test(e.message) ? e.message : fallback;
  return { message, field: e?.details?.field, code: e?.code };
}
